import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import GoGetFormDialog, { COLORS, METRICS } from "@/components/GoGetFormDialog";
import { Plus, Pencil, Trash2, Target, Trophy, Calendar } from "lucide-react";
import { format } from "date-fns";
import { fmtDate as fmtDateLocal } from "@/lib/formatDate";
import { toast } from "sonner";

const colorMap = Object.fromEntries(COLORS.map((c) => [c.key, c]));
const metricLabel = Object.fromEntries(METRICS.map((m) => [m.key, m.label]));
const isMoneyMetric = (key) => METRICS.find((m) => m.key === key)?.isMoney;

function fmtMoney(n) {
    if (!n && n !== 0) return "$0";
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
}
function fmtCount(n) {
    return Math.round(n || 0).toLocaleString();
}
function fmtValue(metric, n) {
    return isMoneyMetric(metric) ? fmtMoney(n) : fmtCount(n);
}
function fmtDate(d) {
    return fmtDateLocal(d);
}

function GoGetCard({ g, onEdit, onDelete }) {
    const c = colorMap[g.color] || colorMap.blue;
    const pct = Math.min(100, g.progress_pct || 0);
    const overflow = (g.progress_pct || 0) > 100;
    return (
        <div className="ph-card relative overflow-hidden hover:shadow-ph-soft transition-shadow" data-testid={`go-get-card-${g.id}`}>
            {/* Color accent stripe */}
            <div className="h-1.5" style={{ backgroundColor: c.solid }} />
            <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold text-ph-text leading-tight" title={g.name}>{g.name}</div>
                        <div className="text-xs text-ph-muted mt-0.5">{metricLabel[g.metric] || g.metric}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={() => onEdit(g)}
                            className="text-ph-muted hover:text-ph-primary p-1.5 rounded hover:bg-ph-primarySoft transition-colors"
                            data-testid={`edit-go-get-${g.id}`}
                            title="Edit"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => onDelete(g)}
                            className="text-ph-muted hover:text-rose-600 p-1.5 rounded hover:bg-rose-50 transition-colors"
                            data-testid={`delete-go-get-${g.id}`}
                            title="Delete"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>

                {g.description && <div className="text-xs text-ph-muted mb-3">{g.description}</div>}

                {/* Progress */}
                <div className="mt-4">
                    <div className="flex items-baseline justify-between mb-1.5">
                        <div>
                            <span className="text-2xl font-semibold text-ph-text tabular-nums">
                                {fmtValue(g.metric, g.current_value)}
                            </span>
                            <span className="text-sm text-ph-muted ml-1">/ {fmtValue(g.metric, g.target)}</span>
                        </div>
                        <span
                            className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: c.soft, color: c.text }}
                            data-testid={`go-get-pct-${g.id}`}
                        >
                            {(g.progress_pct || 0).toFixed(1)}%
                        </span>
                    </div>
                    <div className="h-2 rounded-full bg-ph-borderSoft overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: c.solid }} />
                    </div>
                </div>

                {/* Footer: status + period */}
                <div className="mt-4 flex items-center justify-between text-xs">
                    {g.status === "Achieved" || overflow ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                            <Trophy className="w-3 h-3" /> Achieved
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                            <Target className="w-3 h-3" /> In progress
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-ph-muted">
                        <Calendar className="w-3 h-3" />
                        {fmtDate(g.period_start)} → {fmtDate(g.period_end)}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default function GoGets() {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deletingNow, setDeletingNow] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/go-gets");
            setGoals(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load Go Gets");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async () => {
        if (!deleting) return;
        setDeletingNow(true);
        try {
            await api.delete(`/go-gets/${deleting.id}`);
            toast.success(`Deleted "${deleting.name}"`);
            setDeleting(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        } finally {
            setDeletingNow(false);
        }
    };

    const totalGoals = goals.length;
    const achieved = goals.filter((g) => (g.progress_pct || 0) >= 100).length;
    const avgProgress = goals.length
        ? Math.round(goals.reduce((s, g) => s + Math.min(100, g.progress_pct || 0), 0) / goals.length)
        : 0;

    return (
        <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="go-gets-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Go Gets</h1>
                    <p className="text-sm text-ph-muted mt-1">
                        The goals we're working toward. Auto-tracked from your pipeline data — except for manual goals, which you update yourself.
                    </p>
                </div>
                <Button
                    onClick={() => { setEditing(null); setFormOpen(true); }}
                    className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover"
                    data-testid="add-go-get-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> New Go Get
                </Button>
            </div>

            {totalGoals > 0 && (
                <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="ph-card p-4">
                        <div className="ph-label">Total goals</div>
                        <div className="text-2xl font-semibold text-ph-text tabular-nums mt-1">{totalGoals}</div>
                    </div>
                    <div className="ph-card p-4">
                        <div className="ph-label">Achieved</div>
                        <div className="text-2xl font-semibold text-emerald-600 tabular-nums mt-1">{achieved}</div>
                    </div>
                    <div className="ph-card p-4">
                        <div className="ph-label">Avg progress</div>
                        <div className="text-2xl font-semibold text-ph-primary tabular-nums mt-1">{avgProgress}%</div>
                    </div>
                </section>
            )}

            {loading && (
                <div className="ph-card p-12 text-center text-ph-muted">Loading…</div>
            )}

            {!loading && totalGoals === 0 && (
                <div className="ph-card p-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-ph-primarySoft mx-auto flex items-center justify-center mb-4">
                        <Target className="w-6 h-6 text-ph-primary" />
                    </div>
                    <h2 className="text-lg font-semibold text-ph-text">No goals yet</h2>
                    <p className="text-sm text-ph-muted mt-1 max-w-md mx-auto">
                        Add your first Go Get — like "$5M Bound Premium 2026" or "50 New Active Brokers" — and we'll track progress automatically as your pipeline grows.
                    </p>
                    <Button
                        onClick={() => { setEditing(null); setFormOpen(true); }}
                        className="mt-5 bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                        data-testid="empty-add-go-get-btn"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Add your first Go Get
                    </Button>
                </div>
            )}

            {!loading && totalGoals > 0 && (
                <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="go-gets-grid">
                    {goals.map((g) => (
                        <GoGetCard
                            key={g.id}
                            g={g}
                            onEdit={(g) => { setEditing(g); setFormOpen(true); }}
                            onDelete={(g) => setDeleting(g)}
                        />
                    ))}
                </section>
            )}

            <GoGetFormDialog
                open={formOpen}
                onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
                goGet={editing}
                onSuccess={load}
            />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this Go Get?</AlertDialogTitle>
                        <AlertDialogDescription>
                            "{deleting?.name}" will be permanently removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deletingNow}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                        >
                            {deletingNow ? "Deleting…" : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
